import React, { type ReactNode, useEffect, useRef } from 'react';
import {
	ContextualAnalyticsData,
	SCREEN,
	fireTrackAnalytics,
	fireScreenAnalytics,
	useAnalyticsEvents,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import { useTableFilterStore } from '../../controllers/top-filter';
import { useProjectAnalyticsAttributes } from '../../services/get-project';
import { useHistoricalCycleTimeData } from '../../services/historical-cycle-time';
import { isDataEmpty } from '../../services/historical-cycle-time/utils';

type Props = {
	hasProviders: boolean;
	children: ReactNode;
};

// The component is not part of CycleTimeDataAnalyticsWrapper since it needs to access to ContextualAnalyticsData
const CycleTimeAnalytics = ({ hasProviders }: { hasProviders: boolean }) => {
	const projectInfo = useProjectAnalyticsAttributes();
	const projectId: ProjectId | null = projectInfo?.projectId ?? null;
	const [filters] = useTableFilterStore();
	const { data, loading } = useHistoricalCycleTimeData(projectId, filters);
	const hasFiredRef = useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (!loading && data && data.barData) {
			const attributes: Attributes = {
				hasSufficientData: !isDataEmpty(data.barData),
				hasProviders,
			};
			fireScreenAnalytics(createAnalyticsEvent({}), attributes);
			fireTrackAnalytics(createAnalyticsEvent({}), 'report viewed', 'cycleTime', attributes);
			hasFiredRef.current = true;
		}
	}, [createAnalyticsEvent, data, hasProviders, loading]);

	return null;
};

const CycleTimeDataAnalyticsWrapper = ({ children, hasProviders }: Props) => {
	const projectInfo = useProjectAnalyticsAttributes();

	return (
		<ContextualAnalyticsData
			sourceType={SCREEN}
			sourceName="cycleTimeReportPage"
			attributes={{ ...projectInfo }}
		>
			<CycleTimeAnalytics hasProviders={hasProviders} />
			{children}
		</ContextualAnalyticsData>
	);
};

export default CycleTimeDataAnalyticsWrapper;
